import { useState, useEffect } from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Headset, Receipt, SquaresFour, SignOut, Buildings, Compass, Package, List, X } from "@phosphor-icons/react";

// Corrección de arquitectura (2026-09-14): Equipos y la consola founder de
// Billing ahora viven aquí, reusando EquiposPage.js y BillingPage.jsx tal
// cual (mismo componente, mismo backend/team_id — cero cambio de schema).
// Naming en UI generalizado a "Negocios / Workspaces" para reflejar que
// Call Center es un workspace más, no el centro administrativo — el
// backend sigue hablando de team_id/equipos internamente, sin tocar.
const navItems = [
  { to: "/admin-central", label: "Dashboard", icon: SquaresFour, end: true },
  { to: "/admin-central/equipos", label: "Negocios / Workspaces", icon: Buildings },
  { to: "/admin-central/facturacion", label: "Facturación y Suscripciones", icon: Receipt },
  // FASE 1 — Ruta Comercial (Founder, 2026-09-18): recorrido de
  // prospectos de Commandix, dominio propio, no confundir con "Negocios
  // / Workspaces" (tenants ya facturando).
  { to: "/admin-central/ruta-comercial", label: "Ruta Comercial / Clientes", icon: Compass },
  // Commandix Labels (Ticket Maestro, 2026-09-26): módulo nuevo e
  // independiente — no confundir con "Negocios/Workspaces" ni Ruta Comercial.
  { to: "/admin-central/labels", label: "Commandix Labels", icon: Package },
];

export default function AdminCentralLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Pedido explícito del Founder (2026-10-02, probado en un teléfono real
  // vía emulación de Chrome DevTools -- no solo achicando la ventana): en
  // pantalla de teléfono el menú lateral se quedaba fijo ocupando media
  // pantalla. Ahora se esconde detrás de un ícono de hamburguesa (☰) y dejar
  // el contenido con la pantalla completa -- solo aplica por debajo de
  // `ac-mobile-breakpoint` (CSS), en pantallas grandes el sidebar sigue
  // fijo exactamente como siempre.
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false); // cerrar solo al cambiar de sección, nunca a mitad de una pantalla
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="ac-root">
      <style>{`
        .ac-root {
          min-height: 100vh;
          display: flex;
          background: #0a0a0c;
          font-family: 'Inter', -apple-system, sans-serif;
        }
        .ac-sidebar {
          width: 264px;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          background: #0d0d10;
          border-right: 1px solid rgba(255,255,255,0.07);
          padding-top: env(safe-area-inset-top);
          padding-bottom: env(safe-area-inset-bottom);
        }
        .ac-brand {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 0 22px;
          height: 80px;
          border-bottom: 1px solid rgba(255,255,255,0.07);
        }
        .ac-brand-logo {
          height: 24px;
          object-fit: contain;
          flex-shrink: 0;
        }
        .ac-brand-divider {
          width: 1px;
          height: 24px;
          background: rgba(255,255,255,0.10);
          flex-shrink: 0;
        }
        .ac-brand-text { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
        .ac-brand-name {
          font-family: 'Sora', 'Inter', sans-serif;
          font-weight: 600;
          font-size: 13.5px;
          letter-spacing: -0.01em;
          color: #f5f5f5;
          line-height: 1;
          white-space: nowrap;
        }
        .ac-brand-sub {
          font-family: 'JetBrains Mono', monospace;
          font-size: 9px;
          letter-spacing: 0.12em;
          color: #5b9dd6;
          line-height: 1;
        }

        .ac-switcher-wrap { padding: 14px 14px 0; }
        .ac-switcher {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 600;
          color: #d5d5d9;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 10px;
          padding: 9px;
          cursor: pointer;
          transition: background 160ms ease, border-color 160ms ease, transform 160ms ease;
        }
        .ac-switcher:hover { background: rgba(255,138,0,0.08); border-color: rgba(255,138,0,0.3); color: #ffa64d; }
        .ac-switcher:active { transform: scale(0.98); }

        .ac-nav { flex: 1; padding: 18px 12px; display: flex; flex-direction: column; gap: 3px; }
        .ac-nav-item {
          display: flex;
          align-items: center;
          gap: 11px;
          padding: 10px 12px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 500;
          color: #9a9aa2;
          text-decoration: none;
          transition: background 140ms ease, color 140ms ease;
        }
        .ac-nav-item:hover { background: rgba(255,255,255,0.045); color: #f5f5f5; }
        .ac-nav-item.active { background: rgba(91,157,214,0.12); color: #7fb2e0; }

        .ac-footer {
          border-top: 1px solid rgba(255,255,255,0.07);
          padding: 12px 14px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .ac-avatar {
          width: 32px;
          height: 32px;
          border-radius: 999px;
          background: rgba(91,157,214,0.18);
          color: #7fb2e0;
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .ac-user-name { font-size: 12px; font-weight: 600; color: #f5f5f5; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ac-user-email { font-size: 10px; color: #6b6b72; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ac-logout {
          background: none;
          border: none;
          color: #6b6b72;
          cursor: pointer;
          padding: 6px;
          border-radius: 8px;
          display: flex;
          transition: color 140ms ease, background 140ms ease;
        }
        .ac-logout:hover { color: #f5f5f5; background: rgba(255,255,255,0.06); }

        .ac-main { flex: 1; min-width: 0; overflow-y: auto; }

        .ac-mobile-topbar { display: none; }
        .ac-sidebar-backdrop { display: none; }

        /* Breakpoint móvil (2026-10-02, pedido real tras probar en un
           teléfono real): el sidebar deja de ocupar espacio fijo y se
           vuelve un panel que se desliza por encima del contenido. */
        @media (max-width: 860px) {
          .ac-root { display: block; }
          .ac-mobile-topbar {
            display: flex;
            align-items: center;
            gap: 12px;
            /* Pedido explícito del Founder (2026-10-15, teléfono real): el
            botón de menú quedaba pegado arriba del todo (zona del
            notch/status bar) y a veces no se podía tocar. min-height en vez
            de height fijo deja que la cabecera crezca si el padding lo
            necesita, y se suma ~10px extra sobre el safe-area-inset-top.
            Sigue siendo sticky (no fixed) así que el contenido de abajo se
            corre solo, sin tapar nada. Solo aplica dentro de este
            @media (max-width: 860px). */
            min-height: 56px;
            padding: 0 14px;
            padding-top: calc(env(safe-area-inset-top) + 10px);
            padding-bottom: 8px;
            background: #0d0d10;
            border-bottom: 1px solid rgba(255,255,255,0.07);
            position: sticky;
            top: 0;
            z-index: 30;
          }
          .ac-mobile-menu-btn {
            background: none;
            border: 1px solid rgba(255,255,255,0.12);
            color: #f5f5f5;
            /* 44x44px — zona de toque mínima recomendada, solo en móvil. */
            width: 44px;
            height: 44px;
            border-radius: 9px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            cursor: pointer;
          }
          .ac-mobile-menu-btn:active { background: rgba(255,255,255,0.08); }
          .ac-mobile-topbar-logo { height: 20px; object-fit: contain; }

          .ac-sidebar {
            position: fixed;
            top: 0;
            left: 0;
            bottom: 0;
            z-index: 50;
            width: 86vw;
            max-width: 300px;
            transform: translateX(-100%);
            transition: transform 220ms ease;
            box-shadow: 2px 0 24px rgba(0,0,0,0.5);
          }
          .ac-sidebar.ac-sidebar-open { transform: translateX(0); }

          .ac-sidebar-backdrop {
            display: block;
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.5);
            z-index: 40;
            opacity: 0;
            pointer-events: none;
            transition: opacity 220ms ease;
          }
          .ac-sidebar-backdrop.ac-sidebar-backdrop-open { opacity: 1; pointer-events: auto; }

          .ac-main { width: 100%; }
        }
      `}</style>

      <div className="ac-mobile-topbar">
        <button type="button" className="ac-mobile-menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Abrir menú" data-testid="admin-central-mobile-menu-btn">
          <List size={20} weight="bold" />
        </button>
        <img src="/brand/commandix-logo-tight.png" alt="Commandix" className="ac-mobile-topbar-logo" />
      </div>

      <div className={`ac-sidebar-backdrop${sidebarOpen ? " ac-sidebar-backdrop-open" : ""}`} onClick={() => setSidebarOpen(false)} />

      <aside className={`ac-sidebar${sidebarOpen ? " ac-sidebar-open" : ""}`} data-testid="admin-central-sidebar">
        <div className="ac-brand">
          <img src="/brand/commandix-logo-tight.png" alt="Commandix" className="ac-brand-logo" />
          <div className="ac-brand-divider" />
          <div className="ac-brand-text">
            <div className="ac-brand-name">Admin Central</div>
            <div className="ac-brand-sub">COMMANDIX</div>
          </div>
        </div>

        {/* Switcher — ÚNICO lugar donde puede cambiar de ecosistema.
            Renderizado igual aquí y en el sidebar de Call Center. Solo
            Founder ve este componente en absoluto (gated por el route
            wrapper, ver App.js AdminCentralRoute). */}
        <div className="ac-switcher-wrap">
          <button type="button" className="ac-switcher" onClick={() => navigate("/dashboard")} data-testid="switcher-to-call-center">
            <Headset size={14} weight="bold" />
            Ir a Call Center
          </button>
        </div>

        <nav className="ac-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `ac-nav-item${isActive ? " active" : ""}`}
              data-testid={`admin-central-nav-${item.to.split("/").pop() || "dashboard"}`}
            >
              <item.icon size={18} weight="duotone" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ac-footer">
          <div className="ac-avatar">{(user?.name || "F").slice(0, 2).toUpperCase()}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ac-user-name">{user?.name || "Founder"}</div>
            <div className="ac-user-email">{user?.email}</div>
          </div>
          <button type="button" className="ac-logout" onClick={handleLogout} data-testid="admin-central-logout">
            <SignOut size={16} />
          </button>
        </div>
      </aside>

      <main className="ac-main">{children}</main>
    </div>
  );
}
